import { z } from 'zod';
import { brandKitSchema } from './brand-kit.js';
import { tenantConfigSchema } from './tenant-config.js';

// Authoring format for `ferro tenant create --from-kit ./tenant-kit.json`
// per design §9 (TENANT-PROVISIONING-DESIGN-2026-04-20 §9). This is the
// JSON file a platform engineer writes; the CLI (D1.6.3) converts it
// into a TenantProvisioningSpec (see packages/core/tenant/provisioning.ts)
// before handing it to createTenant(). Wizard and CLI share the same
// commit service but the CLI reads this narrower, more author-friendly
// shape:
//
//   - `modules` is a flat string[] (the CLI maps it into
//     { requestedSlugs } before calling the service).
//   - `domains[].hostname` may be omitted when `kind === 'subdomain'`
//     because the CLI derives it from the slug.
//   - `basic.contractSignedAt` is metadata we want in the kit file for
//     the paper trail but the commit service doesn't persist it.
//   - There is no `actor` field — actor identity comes from whoever
//     runs the CLI, not from the file.

const slugSchema = z
  .string()
  .regex(/^[a-z0-9-]+$/, 'Slug must be lowercase letters, digits, and hyphens only')
  .min(3, 'Slug must be at least 3 characters')
  .max(40, 'Slug must be at most 40 characters');

const hostnameSchema = z
  .string()
  .min(1)
  .max(253)
  .regex(
    /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/,
    'Hostname must be a valid fully-qualified domain',
  );

const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');

const tenantKitDomainKindSchema = z.enum([
  'subdomain',
  'cname_customer',
  'cname_admin',
]);
const tenantKitSurfaceSchema = z.enum(['customer', 'admin']);

export const tenantKitBasicSchema = z.object({
  slug: slugSchema,
  legalName: z.string().min(1),
  displayName: z.string().min(1),
  vertical: z.string().min(1).default('pet-services'),
  contractSignedAt: isoDateSchema.optional(),
});

export const tenantKitDomainSchema = z
  .object({
    kind: tenantKitDomainKindSchema,
    surface: tenantKitSurfaceSchema,
    hostname: hostnameSchema.optional(),
  })
  .refine((d) => d.kind === 'subdomain' || typeof d.hostname === 'string', {
    message: 'hostname is required for cname_customer and cname_admin domains',
    path: ['hostname'],
  });

export const tenantKitInviteModeSchema = z.enum(['magic_link']);

export const tenantKitOwnerSchema = z.object({
  email: z.string().email(),
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
  inviteMode: tenantKitInviteModeSchema.default('magic_link'),
});

export const tenantKitModulesSchema = z
  .array(
    z
      .string()
      .regex(
        /^[a-z0-9-]+$/,
        'Module slug must be lowercase letters, digits, and hyphens only',
      )
      .min(1),
  )
  .min(1, 'At least one module must be requested');

export const tenantKitSchema = z.object({
  basic: tenantKitBasicSchema,
  domains: z
    .array(tenantKitDomainSchema)
    .min(1, 'At least one domain is required'),
  branding: brandKitSchema,
  config: tenantConfigSchema,
  modules: tenantKitModulesSchema,
  owner: tenantKitOwnerSchema,
});

export type TenantKitBasic = z.infer<typeof tenantKitBasicSchema>;
export type TenantKitDomain = z.infer<typeof tenantKitDomainSchema>;
export type TenantKitOwner = z.infer<typeof tenantKitOwnerSchema>;
export type TenantKitInviteMode = z.infer<typeof tenantKitInviteModeSchema>;
export type TenantKitModules = z.infer<typeof tenantKitModulesSchema>;
export type TenantKit = z.infer<typeof tenantKitSchema>;
